import { Button } from '@mui/joy';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Fragment,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '@/design-system/utils/cn';
import { localTime } from '@/utils/utc-time';

import {
  DateTimeRangePickerValue,
  DurationUnit,
  parseRelativeDate,
} from '../RunsTable/utils/getDateTimeRangeLabel';

dayjs.extend(utc);

function useTripleInputFocus() {
  const one = useRef<HTMLInputElement>(null);
  const two = useRef<HTMLInputElement>(null);
  const three = useRef<HTMLInputElement>(null);

  const refs = useMemo(() => [one, two, three], [one, two, three]);

  return {
    refs,
    focusNext: (prevIndex: number) => {
      refs.at(prevIndex + 1)?.current?.focus();
    },
    focusPrev: (prevIndex: number) => {
      refs.at(Math.max(0, prevIndex - 1))?.current?.focus();
    },
  };
}
const DATE_OPTIONS = {
  label: 'Date',
  order: ['yyyy', 'mm', 'dd'],
  format: {
    yyyy: {
      name: 'year',
      range: [1900, Infinity] as [number, number],
    },
    mm: {
      name: 'month',
      range: [1, 12] as [number, number],
    },
    dd: {
      name: 'day',
      range: [1, 31] as [number, number],
    },
  },
  separator: '-',
  displaySeparator: '/',
};

const TIME_OPTIONS = {
  label: 'Time',
  order: ['hh', 'mm', 'ss'],
  format: {
    hh: {
      name: 'hour',
      range: [0, 23] as [number, number],
    },
    mm: {
      name: 'minute',
      range: [0, 59] as [number, number],
    },
    ss: {
      name: 'second',
      range: [0, 59] as [number, number],
    },
  },
  separator: ':',
  displaySeparator: ':',
};

function SegmentedInput(props: {
  value: string;
  onChange: (value: string) => void;
  options: {
    label: string;
    order: string[];
    format: Record<string, { name: string; range: [number, number] }>;
    separator: string;
    displaySeparator: string;
  };
}) {
  const { refs, focusNext, focusPrev } = useTripleInputFocus();
  const [error, setError] = useState<string | null>(null);
  const firstId = useId();

  const value = props.value.split(props.options.separator);

  const prevValue = useRef(props.value);
  useEffect(() => {
    if (prevValue.current !== props.value && props.value === '') {
      for (const ref of refs) {
        const input = ref.current;
        if (input != null) input.value = '';
      }
    }
    prevValue.current = props.value;
  }, [props.options.separator, props.value, refs]);

  const handleChange = (part: string, index: number) => {
    try {
      const numberValue = Number.parseInt(part, 10);

      if (part && Number.isNaN(numberValue))
        throw new Error('Not a valid numeric value');

      const type = props.options.order[index];
      const { name, range } = props.options.format[type];
      if ((part && numberValue < range[0]) || numberValue > range[1]) {
        throw new Error(`Invalid ${name}`);
      }

      if (part.length === type.length) {
        focusNext(index);
      }

      const values = refs.map((i) => i.current?.value);
      values[index] = part;

      if (values.every((i) => i)) {
        props.onChange(
          values
            .map((value, index) =>
              value?.padStart(props.options.order[index].length, '0')
            )
            .join(props.options.separator)
        );
      } else if (values.every((i) => i === '' || i == null)) {
        props.onChange('');
      }

      setError(null);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }
    }
  };

  function getSize(length: number) {
    const target = 14 * length;
    return target - Math.max(0, length - 2) * 6;
  }

  return (
    <div className="group flex flex-col gap-1">
      <label htmlFor={firstId} className="text-sm font-medium text-tertiary">
        {props.options.label}
      </label>
      <div className="flex min-w-[188px] items-center justify-center gap-2 overflow-hidden rounded-lg border border-secondary outline-2 outline-brand-green-400 focus-within:outline">
        {refs.map((ref, index, lst) => (
          <Fragment key={index}>
            <input
              id={index === 0 ? firstId : undefined}
              className="!bg-transparent py-1.5 text-center focus:outline-none"
              style={{ width: getSize(props.options.order[index].length) }}
              placeholder={props.options.order[index].toUpperCase()}
              defaultValue={value[index]}
              ref={ref}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && e.currentTarget.value === '') {
                  focusPrev(index);
                }
              }}
            />

            {index !== lst.length - 1 ? (
              <>{props.options.displaySeparator}</>
            ) : null}
          </Fragment>
        ))}
      </div>
      {error && (
        <div className="text-sm text-error group-focus-within:hidden">
          {error}
        </div>
      )}
    </div>
  );
}

export function SpecificDateTimeInput(props: {
  label?: ReactNode;
  defaultValue: string;
  onChange: (value: string) => void;
  actions?: ReactNode;
  direction?: 'horizontal' | 'vertical';
}) {
  const [date, setDate] = useState(() => {
    return props.defaultValue
      ? localTime(props.defaultValue).format('YYYY-MM-DD')
      : '';
  });
  const [time, setTime] = useState(() => {
    return props.defaultValue
      ? localTime(props.defaultValue).format('HH:mm:ss')
      : '';
  });

  function onChange(value: string, type: 'date' | 'time') {
    let newDate = date;
    let newTime = time;

    if (type === 'date') {
      newDate = value;
    } else if (type === 'time') {
      newTime = value;
    }

    setDate(newDate);
    setTime(newTime);

    if (newDate) {
      const newValue = dayjs(`${newDate}T${newTime || '00:00:00'}.000`);
      // user input is in local timezone, convert back to UTC
      props.onChange(newValue.utc().format());
    } else {
      props.onChange('');
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {props.label && (
        <div className="text-sm font-medium text-tertiary">{props.label}</div>
      )}
      <div
        className={cn(
          'flex flex-col gap-4',
          props.direction === 'horizontal' && 'flex-row'
        )}
      >
        <SegmentedInput
          value={date}
          onChange={(value) => onChange(value, 'date')}
          options={DATE_OPTIONS}
        />
        <SegmentedInput
          value={time}
          onChange={(value) => onChange(value, 'time')}
          options={TIME_OPTIONS}
        />
      </div>
      {props.actions}
    </div>
  );
}

const durationUnitToManipulateUnit = (
  unit: DurationUnit
): dayjs.ManipulateType => {
  const map: Record<DurationUnit, dayjs.ManipulateType> = {
    y: 'year',
    M: 'month',
    w: 'week',
    d: 'day',
    h: 'hour',
    m: 'minute',
    s: 'second',
    ms: 'millisecond',
  };
  return map[unit];
};

export function SpecificDateTime(props: {
  defaultValue: DateTimeRangePickerValue;
  onClose: () => void;
  onSubmit: (value: DateTimeRangePickerValue) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState(() => {
    if (props.defaultValue?.duration) {
      const parsed = parseRelativeDate(props.defaultValue.duration);
      if (parsed) {
        return dayjs
          .utc()
          .subtract(parsed.value, durationUnitToManipulateUnit(parsed.unit))
          .format();
      }
    }
    return props.defaultValue?.start_time ?? '';
  });

  const [end, setEnd] = useState(
    () => props.defaultValue?.end_time ?? dayjs.utc().format()
  );

  const validateDates = () => {
    const startDate = dayjs(start);
    const endDate = dayjs(end);

    if (!startDate.isValid()) {
      setError('Invalid start date');
      return false;
    }

    if (!endDate.isValid()) {
      setError('Invalid end date');
      return false;
    }

    if (startDate.isAfter(endDate)) {
      const sameDay =
        startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD');
      const errorMessage = sameDay
        ? 'Start date must be before end date. Check that your time uses 24-hour format (e.g., 14:30 for 2:30 PM).'
        : 'Start date must be before end date.';
      setError(errorMessage);
      return false;
    }

    return true;
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-4">
        <SpecificDateTimeInput
          label="Start date"
          defaultValue={start}
          onChange={setStart}
        />
        <SpecificDateTimeInput
          label="End date"
          defaultValue={end}
          onChange={setEnd}
        />
      </div>
      {error && (
        <div className="text-sm text-error group-focus-within:hidden">
          {error}
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="plain"
          color="neutral"
          onClick={props.onClose}
          className="text-sm"
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          color="primary"
          onClick={() => {
            if (validateDates()) {
              props.onSubmit({ start_time: start, end_time: end });
              props.onClose();
            }
          }}
          className="text-sm"
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
